<script>
    import { createEventDispatcher, onMount } from 'svelte';
    import { antibodiesStore, antibodies, antibodiesLoading, antibodiesError } from '../../stores/antibodiesStore';
    import { antibodyTableService } from '../../services/antibodyTableService';
    import { generateAllAntibodyParagraphs, copyToClipboard } from '../../services/antibodyService';
    import DraggableResizablePanel from '../shared/DraggableResizablePanel.svelte';
    
    export let show = false;
    
    const dispatch = createEventDispatcher();
    
    let selectedFile = null;
    let tableName = '';
    let isUploading = false;
    let uploadError = '';
    let fileInput;
    let showUploadForm = false;
    let selectedTableForParagraphs = null;
    let generatedParagraphs = [];
    let isGeneratingParagraphs = false;
    let paragraphsError = '';
    let copiedParagraphIds = new Set();
    
    $: if (show) {
        // Fetch tables when modal opens
        antibodiesStore.fetchTables();
    }
    
    function close() {
        dispatch('close');
    }
    
    function handleFileSelect(event) {
        const file = event.target.files[0];
        if (file) {
            selectedFile = file;
            if (!tableName) {
                // Auto-generate table name from filename
                tableName = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
            }
            uploadError = '';
        }
    }
    
    async function handleUpload() {
        if (!selectedFile) {
            uploadError = 'Please select a file';
            return;
        }
        
        if (!tableName.trim()) {
            uploadError = 'Please enter a table name';
            return;
        }
        
        isUploading = true;
        uploadError = '';
        
        try {
            const table = await antibodiesStore.uploadTable(selectedFile, tableName.trim());
            
            if (table) {
                // Successfully uploaded, reset form
                selectedFile = null;
                tableName = '';
                showUploadForm = false;
                if (fileInput) fileInput.value = '';
                
                // If the upload response includes paragraphs, show them
                if (table.paragraphs && table.paragraphs.length > 0) {
                    selectedTableForParagraphs = table;
                    generatedParagraphs = table.paragraphs;
                    copiedParagraphIds = new Set();
                }
            }
        } catch (error) {
            uploadError = error.message || 'Failed to upload table';
        } finally {
            isUploading = false;
        }
    }
    
    function insertTable(table) {
        dispatch('insert', {
            id: table.id,
            name: table.name,
            data: table.data
        });
        // Don't close the panel - keep it open for multiple insertions
    }
    
    async function generateParagraphs(table) {
        selectedTableForParagraphs = table;
        generatedParagraphs = [];
        isGeneratingParagraphs = true;
        paragraphsError = '';
        copiedParagraphIds = new Set();
        
        try {
            const result = await generateAllAntibodyParagraphs(table.data);
            if (result.success) {
                generatedParagraphs = result.paragraphs || [];
            } else {
                paragraphsError = 'Failed to generate paragraphs';
            }
        } catch (error) {
            paragraphsError = error.message || 'Failed to generate paragraphs';
        } finally {
            isGeneratingParagraphs = false;
        }
    }
    
    async function copyParagraph(paragraph) {
        const success = await copyToClipboard(paragraph.text);
        if (success) {
            copiedParagraphIds.add(paragraph.id);
            copiedParagraphIds = copiedParagraphIds; // Trigger reactivity
            
            // Show temporary success feedback
            setTimeout(() => {
                copiedParagraphIds.delete(paragraph.id);
                copiedParagraphIds = copiedParagraphIds;
            }, 2000);
        }
    }
    
    function closeParagraphs() {
        selectedTableForParagraphs = null;
        generatedParagraphs = [];
        paragraphsError = '';
        copiedParagraphIds = new Set();
    }
    
    function toggleUploadForm() {
        showUploadForm = !showUploadForm;
        if (!showUploadForm) {
            selectedFile = null;
            tableName = '';
            uploadError = '';
            if (fileInput) fileInput.value = '';
        }
    }
    
    async function deleteTable(tableId) {
        if (confirm('Are you sure you want to delete this table?')) {
            await antibodiesStore.deleteTable(tableId);
        }
    }
    
    function handleKeydown(event) {
        if (event.key === 'Escape') {
            close();
        }
    }
    
    onMount(() => {
        window.addEventListener('keydown', handleKeydown);
        return () => {
            window.removeEventListener('keydown', handleKeydown);
        };
    });
</script>

{#if show}
    <DraggableResizablePanel 
        {show}
        title="Antibody Tables"
        initialPosition={{ x: 200, y: 150 }}
        initialWidth={700}
        initialHeight={600}
        minWidth={600}
        minHeight={400}
        maxWidth={900}
        maxHeight={800}
        storageKey="antibody-tables"
        theme="navigation"
        on:close={close}
    >
                <div class="modal-actions">
                    <button 
                        class="modal-button modal-button-accent"
                        on:click={toggleUploadForm}
                        disabled={isUploading}
                    >
                        {showUploadForm ? 'Cancel Upload' : 'Upload New Table'}
                    </button>
                    
                    <button
                        class="modal-button modal-button-accent"
                        on:click={() => antibodiesStore.clearCache()}
                    >
                        Refresh
                    </button>
                </div>
                
                {#if showUploadForm}
                    <div class="upload-form">
                        <h3>Upload Antibody Table</h3>
                        <div class="form-group">
                            <label for="table-name">Table Name (for @{'{'}references{'}'}):</label>
                            <input
                                id="table-name"
                                type="text"
                                placeholder="e.g., CTLA4-antibodies"
                                bind:value={tableName}
                                disabled={isUploading}
                            />
                        </div>
                        
                        <div class="form-group">
                            <label for="file-input">Select CSV or Excel file:</label>
                            <input
                                id="file-input"
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                on:change={handleFileSelect}
                                bind:this={fileInput}
                                disabled={isUploading}
                            />
                        </div>
                        
                        {#if selectedFile}
                            <div class="selected-file">
                                Selected: {selectedFile.name}
                            </div>
                        {/if}
                        
                        {#if uploadError}
                            <div class="error-message">
                                {uploadError}
                            </div>
                        {/if}
                        
                        <button 
                            class="modal-button modal-button-accent"
                            on:click={handleUpload}
                            disabled={!selectedFile || !tableName.trim() || isUploading}
                        >
                            {isUploading ? 'Uploading...' : 'Upload Table'}
                        </button>
                    </div>
                {/if}
                
                <div class="tables-list">
                    <h3>Available Tables</h3>
                    
                    {#if $antibodiesLoading}
                        <div class="loading">Loading tables...</div>
                    {:else if $antibodiesError}
                        <div class="error-message">{$antibodiesError}</div>
                    {:else if $antibodies.length === 0}
                        <div class="no-tables">
                            No tables uploaded yet. Upload a CSV or Excel file to get started.
                        </div>
                    {:else}
                        <div class="table-grid">
                            {#each $antibodies as table}
                                <div class="table-card">
                                    <div class="table-info">
                                        <h4>{table.name}</h4>
                                        <p>{table.numRows || 0} antibodies</p>
                                        <p class="filename">{table.filename}</p>
                                        <p class="date">Created: {new Date(table.created).toLocaleDateString()}</p>
                                    </div>
                                    
                                    <div class="table-preview">
                                        <h5>Table Preview:</h5>
                                        <div class="preview-table">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Ab Name</th>
                                                        <th>VH</th>
                                                        <th>VL</th>
                                                        <th>CDR-H1</th>
                                                        <th>CDR-H2</th>
                                                        <th>CDR-H3</th>
                                                        <th>CDR-L1</th>
                                                        <th>CDR-L2</th>
                                                        <th>CDR-L3</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {#each table.data.slice(0, 3) as row}
                                                        <tr>
                                                            <td>{row.Ab_Name}</td>
                                                            <td>{row.VH || '-'}</td>
                                                            <td>{row.VL || '-'}</td>
                                                            <td>{row['CDR-H1'] || '-'}</td>
                                                            <td>{row['CDR-H2'] || '-'}</td>
                                                            <td>{row['CDR-H3'] || '-'}</td>
                                                            <td>{row['CDR-L1'] || '-'}</td>
                                                            <td>{row['CDR-L2'] || '-'}</td>
                                                            <td>{row['CDR-L3'] || '-'}</td>
                                                        </tr>
                                                    {/each}
                                                    {#if table.data.length > 3}
                                                        <tr class="more-rows">
                                                            <td colspan="9">... and {table.data.length - 3} more rows</td>
                                                        </tr>
                                                    {/if}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    
                                    <div class="table-actions">
                                        <button 
                                            class="modal-button modal-button-accent"
                                            on:click={() => insertTable(table)}
                                        >
                                            Insert
                                        </button>
                                        <button 
                                            class="modal-button modal-button-accent"
                                            on:click={() => generateParagraphs(table)}
                                            disabled={isGeneratingParagraphs}
                                        >
                                            Generate Spec Paragraphs
                                        </button>
                                        <button 
                                            class="modal-button modal-button-danger"
                                            on:click={() => deleteTable(table.id)}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            {/each}
                        </div>
                    {/if}
                </div>
                
                {#if selectedTableForParagraphs && (generatedParagraphs.length > 0 || isGeneratingParagraphs || paragraphsError)}
                    <div class="paragraphs-section">
                        <div class="paragraphs-header">
                            <h3>Generated Specification Paragraphs</h3>
                            <button class="close-paragraphs" on:click={closeParagraphs}>×</button>
                        </div>
                        
                        {#if isGeneratingParagraphs}
                            <div class="loading">Generating paragraphs...</div>
                        {:else if paragraphsError}
                            <div class="error-message">{paragraphsError}</div>
                        {:else if generatedParagraphs.length > 0}
                            <p class="instructions">
                                Copy each paragraph below and paste it into the appropriate section of your specification.
                            </p>
                            
                            {#each generatedParagraphs as paragraph}
                                <div class="paragraph-card">
                                    <div class="paragraph-header">
                                        <h4>{paragraph.name}</h4>
                                    </div>
                                    <p class="paragraph-description">{paragraph.description}</p>
                                    <div class="paragraph-text-container">
                                        <pre class="paragraph-text">{paragraph.text}</pre>
                                    </div>
                                    <button 
                                        class="modal-button modal-button-accent {copiedParagraphIds.has(paragraph.id) ? 'copied' : ''}"
                                        on:click={() => copyParagraph(paragraph)}
                                    >
                                        {copiedParagraphIds.has(paragraph.id) ? 'Copied!' : 'Copy to Clipboard'}
                                    </button>
                                </div>
                            {/each}
                        {/if}
                    </div>
                {/if}
    </DraggableResizablePanel>
{/if}

<style>
    
    .modal-actions {
        display: flex;
        gap: var(--spacing-3);
        margin-bottom: var(--spacing-5);
    }
    
    /* Custom button sizes for modal actions */
    .modal-actions .modal-button {
        padding: var(--spacing-2) var(--spacing-4);
        font-size: var(--font-size-sm);
    }
    
    .upload-form {
        padding: var(--spacing-4);
        margin-bottom: var(--spacing-5);
        background: var(--color-surface-content-spec);
        border: 1px solid var(--color-bg-panel-30);
        border-radius: var(--border-radius-lg);
    }
    
    .upload-form h3 {
        margin: 0 0 var(--spacing-4) 0;
        font-size: var(--font-size-lg);
        color: var(--color-text-primary);
    }
    
    .form-group {
        margin-bottom: var(--spacing-4);
    }
    
    .form-group label {
        display: block;
        margin-bottom: var(--spacing-2);
        font-weight: var(--font-weight-medium);
        color: var(--color-text-primary);
    }
    
    .form-group input[type="text"],
    .form-group input[type="file"] {
        width: 100%;
        padding: var(--spacing-2);
        font-size: var(--font-size-sm);
        color: var(--color-text-primary);
        background: var(--color-surface-content-spec);
        border: 1px solid var(--color-bg-panel-30);
        border-radius: var(--border-radius-md);
    }
    
    .selected-file {
        padding: 8px;
        margin: 10px 0;
        font-size: 0.9rem;
        color: var(--color-text-secondary);
        background: var(--color-text-inverse);
        border: 1px solid var(--color-term-border);
        border-radius: 4px;
    }
    
    .upload-form .modal-button {
        padding: var(--spacing-2) var(--spacing-4);
        font-size: var(--font-size-sm);
    }
    
    .tables-list h3 {
        margin-bottom: 15px;
    }
    
    .loading,
    .no-tables {
        padding: 20px;
        text-align: center;
        color: var(--color-text-tertiary);
    }
    
    .error-message {
        padding: 10px;
        margin: 10px 0;
        color: var(--color-error);
        background: var(--color-ambiguous-term-error-bg);
        border-radius: 4px;
    }
    
    .table-grid {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
    }
    
    .table-card {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: flex-start;
        padding: var(--spacing-4);
        background-color: var(--color-surface-content-spec);
        border: 1px solid var(--color-bg-panel-30);
        border-radius: var(--border-radius-xl);
        gap: var(--spacing-3);
    }
    
    .table-info h4 {
        margin: 0;
        font-size: var(--font-size-base);
        color: var(--color-text-primary);
    }
    
    .table-info p {
        margin: var(--spacing-1) 0;
        font-size: var(--font-size-sm);
        color: var(--color-text-secondary);
    }
    
    .table-info .filename {
        font-style: italic;
    }
    
    .table-info .date {
        font-size: 0.8rem;
        color: var(--color-text-tertiary);
    }
    
    .table-preview h5 {
        margin: 0 0 10px;
        font-size: 0.9rem;
        color: var(--color-text-secondary);
    }
    
    .preview-table {
        overflow-x: auto;
        background: var(--color-bg-terms-toolbar);
        border: 1px solid var(--color-term-border);
        border-radius: 4px;
    }
    
    .preview-table table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.75rem;
    }
    
    .preview-table th,
    .preview-table td {
        padding: 6px 8px;
        text-align: left;
        white-space: nowrap;
        border: 1px solid var(--color-term-border);
    }
    
    .preview-table th {
        font-size: 0.7rem;
        font-weight: 600;
        color: var(--color-text-primary);
        background: var(--color-btn-default);
    }
    
    .preview-table td {
        color: var(--color-text-primary);
        background: var(--color-bg-main);
    }
    
    .preview-table .more-rows td {
        text-align: center;
        font-style: italic;
        color: var(--color-text-tertiary);
        background: var(--color-bg-main);
    }
    
    .table-actions {
        display: flex;
        gap: 8px;
        margin-top: 15px;
    }
    
    .table-actions .modal-button {
        padding: var(--spacing-2) var(--spacing-3);
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-medium);
    }
    
    /* Paragraphs Section */
    .paragraphs-section {
        padding-top: var(--spacing-5);
        margin-top: var(--spacing-5);
        border-top: 1px solid var(--color-bg-panel-30);
    }
    
    .paragraphs-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--spacing-4);
    }
    
    .paragraphs-header h3 {
        margin: 0;
        font-size: var(--font-size-lg);
        color: var(--color-text-primary);
    }
    
    .close-paragraphs {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 30px;
        height: 30px;
        padding: 0;
        font-size: 1.5rem;
        color: var(--color-text-secondary);
        background: none;
        border: none;
        cursor: pointer;
    }
    
    .close-paragraphs:hover {
        color: var(--color-text-primary);
    }
    
    .instructions {
        margin-bottom: var(--spacing-4);
        font-size: var(--font-size-sm);
        color: var(--color-text-secondary);
    }
    
    .paragraph-card {
        padding: var(--spacing-4);
        margin-bottom: var(--spacing-4);
        background: var(--color-surface-content-spec);
        border: 1px solid var(--color-bg-panel-30);
        border-radius: var(--border-radius-lg);
    }
    
    .paragraph-header {
        display: flex;
        align-items: center;
        gap: var(--spacing-3);
        margin-bottom: var(--spacing-2);
    }
    
    .paragraph-number {
        padding: var(--spacing-1) var(--spacing-2);
        font-size: var(--font-size-xs);
        font-weight: var(--font-weight-bold);
        color: var(--color-text-primary);
        background: var(--color-bg-accent-100);
        border-radius: var(--border-radius-md);
    }
    
    .paragraph-header h4 {
        margin: 0;
        font-size: var(--font-size-base);
        color: var(--color-text-primary);
    }
    
    .paragraph-description {
        margin-bottom: var(--spacing-3);
        font-size: var(--font-size-sm);
        color: var(--color-text-secondary);
    }
    
    .paragraph-text-container {
        max-height: 200px;
        padding: var(--spacing-3);
        margin-bottom: var(--spacing-3);
        overflow-y: auto;
        background: var(--color-bg-main);
        border: 1px solid var(--color-bg-panel-30);
        border-radius: var(--border-radius-md);
    }
    
    .paragraph-text {
        margin: 0;
        font-family: var(--font-family-mono);
        font-size: var(--font-size-xs);
        line-height: 1.5;
        white-space: pre-wrap;
        color: var(--color-text-primary);
    }
    
    .paragraph-card .modal-button {
        padding: var(--spacing-2) var(--spacing-3);
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-medium);
    }
    
    .modal-button.copied {
        background: var(--color-success, var(--color-button-accent-bg));
        border-color: var(--color-success, var(--color-button-accent-bg));
    }
    
    .modal-button.copied:hover {
        background: var(--color-success-hover, var(--color-button-accent-bg-hover));
        border-color: var(--color-success-hover, var(--color-button-accent-bg-hover));
    }
</style>